import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import {
  GetStandardPrediction,
  GetStandardPredictionEdit,
  GetStandardPredictionTemporal,
  UpdateModifiedData,
  ApplyPredictionPercentages,
  PostGeneratePrediction,
  GetGenerationStandardPredictionTemporal,
  SetSelectedTab,
  SetCurrentFilters,
  ResetModifiedData,
  SetRangeDate,
} from './standard-prediction.actions';
import { StandardPredictionService } from '../../core/services/standard-prediction/standard-prediction.service';
import {
  IGetStandardPredictionResponse,
  IModifiedData,
  IStandardPredictionFilter,
} from '../../core/interfaces/standard-prediction.interface';
import { interval, throwError } from 'rxjs';
import { tap, switchMap, filter, take, catchError } from 'rxjs/operators';
import {
  LoadingHiddeAction,
  LoadingShowAction,
} from '../loading/loading.actions';

export interface StandardPredictionStateModel {
  predictions: IGetStandardPredictionResponse[];
  modifiedData: IModifiedData[];
  loading: boolean;
  error: any;
  currentFilters?: any;
  selectedTab: number;
  filters: IStandardPredictionFilter | null;
  rangeDate: { startDate: string; endDate: string };
}

@State<StandardPredictionStateModel>({
  name: 'standardPrediction',
  defaults: {
    predictions: [],
    modifiedData: [
      {
        id: 'udc',
        description: 'Último dato coincidente',
        value: 100,
      },
      {
        id: 'pudc',
        description: 'Promedio de los últimos datos coincidentes',
        value: 0,
      },
      {
        id: 'rcdtac',
        description:
          'Razón de cambio de un día coincidente respecto a un periodo típico anterior',
        value: 0,
      },
      {
        id: 'rcudc',
        description:
          'Razón de cambio respecto a los últimos datos coincidentes',
        value: 0,
      },
    ],
    loading: false,
    error: null,
    selectedTab: 0,
    filters: null,
    rangeDate: { startDate: '', endDate: '' },
  },
})
@Injectable()
export class StandardPredictionState {
  constructor(private standardPredictionService: StandardPredictionService) {}

  @Selector()
  static getPredictions(state: StandardPredictionStateModel) {
    return state.predictions;
  }

  @Selector()
  static getLoading(state: StandardPredictionStateModel) {
    return state.loading;
  }

  @Selector()
  static getError(state: StandardPredictionStateModel) {
    return state.error;
  }

  @Selector()
  static getModifiedData(state: StandardPredictionStateModel) {
    return state.modifiedData;
  }

  @Selector()
  static getSelectedTab(state: StandardPredictionStateModel) {
    return state.selectedTab;
  }

  @Selector()
  static getCurrentFilters(state: StandardPredictionStateModel) {
    return state.currentFilters;
  }

  @Selector()
  static getFilters(
    state: StandardPredictionStateModel
  ): IStandardPredictionFilter | null {
    return state.filters ?? null;
  }

  @Selector()
  static getRangeDate(state: StandardPredictionStateModel) {
    return state.rangeDate;
  }

  @Action(GetStandardPrediction)
  getStandardPrediction(
    ctx: StateContext<StandardPredictionStateModel>,
    action: GetStandardPrediction
  ) {
    ctx.dispatch(new LoadingShowAction());

    return this.standardPredictionService
      .getStandardPrediction(action.payload)
      .pipe(
        tap({
          next: response => {
            ctx.dispatch(new LoadingHiddeAction());
            ctx.patchState({
              predictions: response,
            });
          },
          error: error => {
            ctx.dispatch(new LoadingHiddeAction());
          },
        })
      );
  }

  @Action(GetStandardPredictionEdit)
  getStandardPredictionEdit(
    ctx: StateContext<StandardPredictionStateModel>,
    action: GetStandardPredictionEdit
  ) {
    ctx.dispatch(new LoadingShowAction());

    return this.standardPredictionService
      .getStandardPredictionEdit(action.payload)
      .pipe(
        tap({
          next: response => {
            ctx.dispatch(new GetStandardPredictionTemporal(response.uuid));
          },
          error: error => {
            ctx.dispatch(new LoadingHiddeAction());
          },
        })
      );
  }

  @Action(GetStandardPredictionTemporal)
  getStandardPredictionTemporal(
    ctx: StateContext<StandardPredictionStateModel>,
    action: GetStandardPredictionTemporal
  ) {
    ctx.dispatch(new LoadingShowAction());
    return interval(2000).pipe(
      switchMap(() =>
        this.standardPredictionService
          .getStandardPredictionTemporal(action.payload)
          .pipe(
            catchError(error => {
              ctx.patchState({ error, loading: false });
              return throwError(() => error);
            })
          )
      ),
      filter(response => {
        ctx.dispatch(new LoadingShowAction());
        return !!response && Array.isArray(response) && response.length > 0;
      }), // solo si hay datos
      take(1),
      tap({
        next: response => {
          ctx.dispatch(new LoadingHiddeAction());
          ctx.patchState({
            predictions: response as IGetStandardPredictionResponse[],
            loading: false,
          });
        },
        error: error => {
          ctx.dispatch(new LoadingHiddeAction());
        },
      })
    );
  }

  @Action(GetGenerationStandardPredictionTemporal)
  GetGenerationStandardPredictionTemporal(
    ctx: StateContext<StandardPredictionStateModel>,
    action: GetGenerationStandardPredictionTemporal
  ) {
    ctx.dispatch(new LoadingShowAction());
    setTimeout(() => {
      ctx.dispatch(new LoadingShowAction());
    }, 1200);

    return interval(5000).pipe(
      switchMap(() =>
        this.standardPredictionService
          .getStandardPredictionTemporal(action.payload)
          .pipe(
            catchError(error => {
              ctx.dispatch(new LoadingHiddeAction());
              ctx.patchState({ error, loading: false });
              return throwError(() => error);
            })
          )
      ),
      filter((response: any) => {
        // Si es un objeto con status 200, detenemos el polling
        if (response?.status === 200) {
          return true;
        }
        // Si es un array vacío, continuamos el polling
        if (Array.isArray(response) && response.length === 0) {
          return false;
        }
        // Si es un array con datos, detenemos el polling
        return Array.isArray(response) && response.length > 0;
      }),
      take(1),
      tap({
        next: (response: any) => {
          ctx.dispatch(new LoadingHiddeAction());
          if (response?.status === 200) {
            // Si recibimos status 200, llamamos a GetStandardPredictionEdit con los filtros guardados
            const state = ctx.getState();
            const request = {
              fechaInicio: state.currentFilters?.fechaInicio,
              fechaFin: state.currentFilters?.fechaFin,
              /*     baseCalculo: state.currentFilters?.baseCalculo, */
              filters: state.currentFilters?.filters,
              /*       mpp: state.currentFilters?.mpp,
            ignoredDates: state.currentFilters?.ignoredDates, */
            };
            ctx.dispatch(new GetStandardPrediction(request));
            ctx.patchState({
              loading: false,
            });
            ctx.dispatch(new LoadingHiddeAction());
          } else if (Array.isArray(response)) {
            ctx.patchState({
              predictions: response as IGetStandardPredictionResponse[],
            });
            ctx.dispatch(new LoadingHiddeAction());
          }
        },
        error: error => {
          ctx.dispatch(new LoadingHiddeAction());
        },
      })
    );
  }

  @Action(PostGeneratePrediction)
  postGeneratePrediction(
    ctx: StateContext<StandardPredictionStateModel>,
    action: PostGeneratePrediction
  ) {
    ctx.dispatch(new LoadingShowAction());
    return this.standardPredictionService
      .postGeneratePrediction(action.payload)
      .pipe(
        tap({
          next: response => {
            if (response && response.uuid) {
              // Guardamos todo el payload, no solo los filtros
              ctx.patchState({ currentFilters: action.payload });
              ctx.dispatch(
                new GetGenerationStandardPredictionTemporal(response.uuid)
              );
            }
          },
          error: error => {
            ctx.dispatch(new LoadingHiddeAction());
          },
        })
      );
  }

  @Action(UpdateModifiedData)
  updateModifiedData(
    ctx: StateContext<StandardPredictionStateModel>,
    action: UpdateModifiedData
  ) {
    const state = ctx.getState();
    const updated = action.payload;
    let modifiedData = state.modifiedData.map(item =>
      item.id === updated.id ? { ...item, value: updated.value } : item
    );

    const total = modifiedData.reduce(
      (sum, item) => sum + Number(item.value),
      0
    );

    if (total > 100) {
      const diff = total - 100;
      for (let i = 0; i < modifiedData.length; i++) {
        if (modifiedData[i].id !== updated.id) {
          modifiedData[i] = {
            ...modifiedData[i],
            value: Math.max(0, modifiedData[i].value - diff),
          };
          break;
        }
      }
    }

    ctx.patchState({ modifiedData });
  }

  @Action(ApplyPredictionPercentages)
  applyPredictionPercentages(ctx: StateContext<StandardPredictionStateModel>) {
    const state = ctx.getState();
    const percentages = state.modifiedData.reduce(
      (acc, item) => {
        acc[item.id] = Number(item.value) / 100;
        return acc;
      },
      {} as Record<string, number>
    );

    const predictions = state.predictions.map(row => ({
      ...row,
      mpp: Math.round(
        (row.udc || 0) * (percentages['udc'] || 0) +
          (row.pudc || 0) * (percentages['pudc'] || 0) +
          (row.rcdtac || 0) * (percentages['rcdtac'] || 0) +
          (row.rcudc || 0) * (percentages['rcudc'] || 0)
      ),
    }));

    ctx.patchState({ predictions });
  }

  @Action(SetSelectedTab)
  setSelectedTab(
    ctx: StateContext<StandardPredictionStateModel>,
    action: SetSelectedTab
  ) {
    ctx.patchState({ selectedTab: action.payload });
  }

  @Action(SetCurrentFilters)
  setCurrentFilters(
    ctx: StateContext<StandardPredictionStateModel>,
    action: SetCurrentFilters
  ) {
    ctx.patchState({ filters: action.payload });
  }

  @Action(ResetModifiedData)
  resetModifiedData(ctx: StateContext<StandardPredictionStateModel>) {
    ctx.patchState({
      modifiedData: [
        {
          id: 'udc',
          description: 'Último dato coincidente',
          value: 100,
        },
        {
          id: 'pudc',
          description: 'Promedio de los últimos datos coincidentes',
          value: 0,
        },
        {
          id: 'rcdtac',
          description:
            'Razón de cambio de un día coincidente respecto a un periodo típico anterior',
          value: 0,
        },
        {
          id: 'rcudc',
          description:
            'Razón de cambio respecto a los últimos datos coincidentes',
          value: 0,
        },
      ],
    });
  }

  @Action(SetRangeDate)
  setRangeDate(
    ctx: StateContext<StandardPredictionStateModel>,
    action: SetRangeDate
  ) {
    ctx.patchState({ rangeDate: action.payload });
  }
}
